import React, { useState, useEffect, useCallback } from 'react';
import {
	Card,
	CardContent,
	Typography,
	CircularProgress,
	Alert,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	TextField,
	IconButton,
	Collapse,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormControlLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	Grid,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import { Title, useNotify } from 'react-admin';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
	LLMTableSummary,
	LLMFieldSummary,
	getLLMTables,
	getLLMTable,
	updateLLMTable,
	updateLLMField,
	getLLMSemanticCodes,
	syncLLM,
	exportFieldsCSV,
	importFieldsCSV,
	getConnectorSKUs,
	UpdateLLMTableInput,
	UpdateLLMFieldInput,
	LLMSyncResult,
	ImportCSVResult,
} from '../../Services/quantiAI';

// Préfixes semantic code avec descriptions
const SEMANTIC_PREFIXES = [
	{ value: 'e_', label: 'e_ (Entité)', description: 'Identifiants : e_campaign_id, e_order_id' },
	{ value: 'd_', label: 'd_ (Dimension)', description: 'Attributs : d_date, d_country, d_device' },
	{ value: 'm_', label: 'm_ (Mesure)', description: 'Valeurs brutes : m_cost, m_clicks, m_impressions' },
	{ value: 'mt_', label: 'mt_ (Métrique)', description: 'Calculées : mt_roas, mt_cpa, mt_ctr' },
];

// Composant pour une ligne de table expandable
const TableRowExpand = ({
	table,
	onEditTable,
	onEditField,
}: {
	table: LLMTableSummary;
	onEditTable: (t: LLMTableSummary) => void;
	onEditField: (tableId: string, field: LLMFieldSummary) => void;
}) => {
	const [open, setOpen] = useState(false);
	const [fields, setFields] = useState<LLMFieldSummary[]>([]);
	const [loadingFields, setLoadingFields] = useState(false);

	const loadFields = async () => {
		if (fields.length > 0) return; // Déjà chargé
		setLoadingFields(true);
		try {
			const detail = await getLLMTable(table.id);
			setFields(detail.fields || []);
		} catch (err) {
			console.error('Erreur chargement fields:', err);
		} finally {
			setLoadingFields(false);
		}
	};

	const handleToggle = () => {
		if (!open) loadFields();
		setOpen(!open);
	};

	return (
		<>
			<TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
				<TableCell>
					<IconButton size="small" onClick={handleToggle}>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</TableCell>
				<TableCell>
					<Chip label={table.sku} size="small" color="primary" variant="outlined" />
				</TableCell>
				<TableCell>{table.dw_table_name || table.table_id}</TableCell>
				<TableCell>{table.name}</TableCell>
				<TableCell>
					{table.semantic_code ? (
						<Chip
							label={table.semantic_code}
							size="small"
							color="info"
							sx={{ fontFamily: 'monospace' }}
						/>
					) : (
						<Typography variant="body2" color="text.secondary">-</Typography>
					)}
				</TableCell>
				<TableCell>{table.business_domain || '-'}</TableCell>
				<TableCell>
					{table.is_prebuild ? (
						<Chip label="Prebuild" color="info" size="small" />
					) : (
						<Chip label="Override" color="warning" size="small" />
					)}
				</TableCell>
				<TableCell>{table.fields_count}</TableCell>
				<TableCell>
					<Switch checked={table.is_active} disabled size="small" />
				</TableCell>
				<TableCell>
					<IconButton size="small" onClick={() => onEditTable(table)}>
						<EditIcon />
					</IconButton>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box sx={{ margin: 2 }}>
							<Typography variant="subtitle2" gutterBottom>
								Fields ({fields.length})
							</Typography>
							{loadingFields ? (
								<CircularProgress size={20} />
							) : fields.length > 0 ? (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Field ID</TableCell>
											<TableCell>Type</TableCell>
											<TableCell>Semantic Code</TableCell>
											<TableCell>Business Name</TableCell>
											<TableCell>Description</TableCell>
											<TableCell>Flags</TableCell>
											<TableCell>Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{fields.map((field) => (
											<TableRow key={field.id}>
												<TableCell>
													<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
														{field.field_id}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip label={field.type} size="small" variant="outlined" />
												</TableCell>
												<TableCell>
													{field.semantic_code ? (
														<Chip
															label={field.semantic_code}
															size="small"
															color={
																field.semantic_code.startsWith('e_') ? 'primary' :
																field.semantic_code.startsWith('d_') ? 'secondary' :
																field.semantic_code.startsWith('m_') ? 'success' :
																field.semantic_code.startsWith('mt_') ? 'warning' :
																'default'
															}
														/>
													) : (
														<Typography variant="body2" color="text.secondary">-</Typography>
													)}
												</TableCell>
												<TableCell>{field.business_name || '-'}</TableCell>
												<TableCell>
													<Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
														{field.description || '-'}
													</Typography>
												</TableCell>
												<TableCell>
													{field.is_metric && <Chip label="Metric" size="small" sx={{ mr: 0.5 }} />}
													{field.is_pii && <Chip label="PII" color="warning" size="small" sx={{ mr: 0.5 }} />}
													{field.is_hidden && <Chip label="Hidden" size="small" />}
												</TableCell>
												<TableCell>
													<IconButton size="small" onClick={() => onEditField(table.id, field)}>
														<EditIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<Typography variant="body2" color="text.secondary">
									Aucun field enrichi
								</Typography>
							)}
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

// Dialog d'édition de table
const EditTableDialog = ({
	open,
	table,
	onClose,
	onSave,
}: {
	open: boolean;
	table: LLMTableSummary | null;
	onClose: () => void;
	onSave: (id: string, data: UpdateLLMTableInput) => Promise<void>;
}) => {
	const [formData, setFormData] = useState<UpdateLLMTableInput>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (table) {
			setFormData({
				name: table.name,
				description: table.description,
				business_domain: table.business_domain,
				semantic_code: table.semantic_code,
				is_active: table.is_active,
			});
		}
	}, [table]);

	const handleSave = async () => {
		if (!table) return;
		setSaving(true);
		try {
			await onSave(table.id, formData);
			onClose();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Éditer la table</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 1 }}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Nom"
							value={formData.name || ''}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							multiline
							rows={3}
							label="Description"
							value={formData.description || ''}
							onChange={(e) => setFormData({ ...formData, description: e.target.value })}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Business Domain"
							value={formData.business_domain || ''}
							onChange={(e) => setFormData({ ...formData, business_domain: e.target.value })}
							placeholder="performance, attribution, acquisition..."
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Semantic Code"
							value={formData.semantic_code || ''}
							onChange={(e) => setFormData({ ...formData, semantic_code: e.target.value })}
							placeholder="t_campaigns, t_conversions, t_ads..."
							helperText="Code sémantique pour identifier la table (ex: t_campaigns)"
							sx={{ fontFamily: 'monospace' }}
						/>
					</Grid>
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={formData.is_active ?? true}
									onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
								/>
							}
							label="Active"
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Annuler</Button>
				<Button variant="contained" onClick={handleSave} disabled={saving}>
					{saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// Dialog d'édition de field
const EditFieldDialog = ({
	open,
	tableId,
	field,
	semanticCodes,
	onClose,
	onSave,
}: {
	open: boolean;
	tableId: string | null;
	field: LLMFieldSummary | null;
	semanticCodes: string[];
	onClose: () => void;
	onSave: (tableId: string, fieldId: string, data: Partial<UpdateLLMFieldInput>) => Promise<void>;
}) => {
	const [formData, setFormData] = useState<Partial<UpdateLLMFieldInput>>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (field) {
			setFormData({
				semantic_code: field.semantic_code,
				description: field.description,
				business_name: field.business_name,
				is_metric: field.is_metric,
				is_pii: field.is_pii,
				is_hidden: field.is_hidden,
			});
		}
	}, [field]);

	const handleSave = async () => {
		if (!tableId || !field) return;
		setSaving(true);
		try {
			await onSave(tableId, field.field_id, formData);
			onClose();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Éditer le field: <code>{field?.field_id}</code>
			</DialogTitle>
			<DialogContent>
				<Grid container spacing={2} sx={{ mt: 1 }}>
					<Grid item xs={12}>
						<Autocomplete
							freeSolo
							options={semanticCodes}
							value={formData.semantic_code || ''}
							onChange={(_, value) => setFormData({ ...formData, semantic_code: value || undefined })}
							onInputChange={(_, value) => setFormData({ ...formData, semantic_code: value || undefined })}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Semantic Code"
									placeholder="e_campaign_id, d_date, m_cost, mt_roas..."
									helperText={
										<Box component="span">
											{SEMANTIC_PREFIXES.map((p) => (
												<Typography key={p.value} variant="caption" display="block">
													<strong>{p.label}</strong>: {p.description}
												</Typography>
											))}
										</Box>
									}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Business Name"
							value={formData.business_name || ''}
							onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
							placeholder="Nom métier (Dépenses, Clics...)"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							fullWidth
							multiline
							rows={2}
							label="Description"
							value={formData.description || ''}
							onChange={(e) => setFormData({ ...formData, description: e.target.value })}
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							control={
								<Switch
									checked={formData.is_metric ?? false}
									onChange={(e) => setFormData({ ...formData, is_metric: e.target.checked })}
								/>
							}
							label="Metric"
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							control={
								<Switch
									checked={formData.is_pii ?? false}
									onChange={(e) => setFormData({ ...formData, is_pii: e.target.checked })}
								/>
							}
							label="PII"
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							control={
								<Switch
									checked={formData.is_hidden ?? false}
									onChange={(e) => setFormData({ ...formData, is_hidden: e.target.checked })}
								/>
							}
							label="Hidden"
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Annuler</Button>
				<Button variant="contained" onClick={handleSave} disabled={saving}>
					{saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// Composant principal
export const AIEnrichmentsList = () => {
	const [tables, setTables] = useState<LLMTableSummary[]>([]);
	const [loading, setLoading] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [importing, setImporting] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedSku, setSelectedSku] = useState<string>('');
	const [onlyPrebuilds, setOnlyPrebuilds] = useState(true);
	const [semanticCodes, setSemanticCodes] = useState<string[]>([]);
	const [availableSKUs, setAvailableSKUs] = useState<string[]>([]);
	const [lastSyncResult, setLastSyncResult] = useState<LLMSyncResult | null>(null);
	const [lastImportResult, setLastImportResult] = useState<ImportCSVResult | null>(null);

	// Dialogs
	const [editTableDialog, setEditTableDialog] = useState<{ open: boolean; table: LLMTableSummary | null }>({
		open: false,
		table: null,
	});
	const [editFieldDialog, setEditFieldDialog] = useState<{
		open: boolean;
		tableId: string | null;
		field: LLMFieldSummary | null;
	}>({ open: false, tableId: null, field: null });

	const notify = useNotify();

	// Charger les SKUs et semantic codes au démarrage
	useEffect(() => {
		const loadInitialData = async () => {
			try {
				const [skus, codes] = await Promise.all([
					getConnectorSKUs(),
					getLLMSemanticCodes(),
				]);
				setAvailableSKUs(skus);
				setSemanticCodes(codes.codes || []);
			} catch (err) {
				console.error('Erreur chargement données initiales:', err);
			}
		};
		loadInitialData();
	}, []);

	// Charger les tables
	const loadTables = useCallback(async () => {
		if (!selectedSku) {
			setTables([]);
			return;
		}

		setLoading(true);
		setError(null);
		try {
			const response = await getLLMTables({
				sku: selectedSku,
				only_prebuilds: onlyPrebuilds,
			});
			setTables(response.tables || []);
		} catch (err: any) {
			setError(err.message || 'Erreur lors du chargement des tables');
		} finally {
			setLoading(false);
		}
	}, [selectedSku, onlyPrebuilds]);

	useEffect(() => {
		loadTables();
	}, [loadTables]);

	// Handlers
	const handleEditTable = (table: LLMTableSummary) => {
		setEditTableDialog({ open: true, table });
	};

	const handleEditField = (tableId: string, field: LLMFieldSummary) => {
		setEditFieldDialog({ open: true, tableId, field });
	};

	const handleSaveTable = async (id: string, data: UpdateLLMTableInput) => {
		try {
			await updateLLMTable(id, data);
			notify('Table mise à jour', { type: 'success' });
			loadTables(); // Refresh
		} catch (err: any) {
			notify(err.message || 'Erreur', { type: 'error' });
			throw err;
		}
	};

	const handleSaveField = async (tableId: string, fieldId: string, data: Partial<UpdateLLMFieldInput>) => {
		try {
			await updateLLMField(tableId, fieldId, data);
			notify('Field mis à jour', { type: 'success' });
			// Note: les fields sont rechargés au prochain expand
		} catch (err: any) {
			notify(err.message || 'Erreur', { type: 'error' });
			throw err;
		}
	};

	// Sync depuis GitLab
	const handleSync = async () => {
		if (!selectedSku) return;
		setSyncing(true);
		setLastSyncResult(null);
		try {
			const response = await syncLLM(selectedSku);
			setLastSyncResult(response.result);
			const { result } = response;
			const durationMs = Math.round(result.duration / 1_000_000);
			const skipped = result.pushes_skipped ?? 0;
			// Un connecteur reverse ne produit aucune table : tous les compteurs sont
			// à 0 et seul pushes_skipped est renseigné. Afficher les zéros seuls
			// laisserait croire que le sync n'a rien fait, ou a échoué.
			const summary =
				skipped > 0 && result.tables_created === 0 && result.tables_updated === 0
					? `${skipped} push${skipped > 1 ? 'es' : ''} ignoré${skipped > 1 ? 's' : ''} (connecteur reverse : contrats de mapping, pas de table)`
					: `${result.tables_created} tables créées, ${result.tables_updated} mises à jour, ${result.fields_created} fields créés, ${result.fields_updated} mis à jour`;
			notify(`Sync ${selectedSku} terminé : ${summary} (${durationMs}ms)`, {
				type: 'success',
			});
			loadTables(); // Rafraîchir la liste
		} catch (err: any) {
			notify(err.message || 'Erreur lors du sync', { type: 'error' });
		} finally {
			setSyncing(false);
		}
	};

	// Export CSV
	const handleExportCSV = async () => {
		if (!selectedSku) return;
		setExporting(true);
		try {
			await exportFieldsCSV(selectedSku);
			notify(`Export CSV ${selectedSku}_fields.csv téléchargé`, { type: 'success' });
		} catch (err: any) {
			notify(err.message || 'Erreur lors de l\'export', { type: 'error' });
		} finally {
			setExporting(false);
		}
	};

	// Import CSV via drag & drop
	const handleImportCSV = async (file: File) => {
		if (!file.name.endsWith('.csv')) {
			notify('Le fichier doit être au format CSV', { type: 'error' });
			return;
		}
		setImporting(true);
		setLastImportResult(null);
		try {
			const result = await importFieldsCSV(file);
			setLastImportResult(result);
			const message = `Import terminé : ${result.updated} mis à jour, ${result.skipped} ignorés${result.errors?.length ? `, ${result.errors.length} erreurs` : ''}`;
			notify(message, { type: result.errors?.length ? 'warning' : 'success' });
			loadTables(); // Rafraîchir
		} catch (err: any) {
			notify(err.message || 'Erreur lors de l\'import', { type: 'error' });
		} finally {
			setImporting(false);
		}
	};

	// Drag & drop handlers
	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const files = e.dataTransfer.files;
		if (files.length > 0) {
			handleImportCSV(files[0]);
		}
	};

	return (
		<Card>
			<Title title="Quanti AI - Enrichissements LLM" />
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
					<Typography variant="h5">Enrichissements LLM</Typography>
					<Typography variant="body2" color="text.secondary">
						(Tables et Fields pour le Semantic Layer)
					</Typography>
				</Box>

				{/* Filtres */}
				<Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
					<FormControl sx={{ minWidth: 200 }}>
						<InputLabel>Connecteur (SKU)</InputLabel>
						<Select
							value={selectedSku}
							label="Connecteur (SKU)"
							onChange={(e) => setSelectedSku(e.target.value)}
						>
							<MenuItem value="">
								<em>Sélectionner...</em>
							</MenuItem>
							{availableSKUs.map((sku) => (
								<MenuItem key={sku} value={sku}>
									{sku}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControlLabel
						control={
							<Switch
								checked={onlyPrebuilds}
								onChange={(e) => setOnlyPrebuilds(e.target.checked)}
							/>
						}
						label="Prebuilds uniquement"
					/>

					<Button
						variant="outlined"
						startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
						onClick={handleSync}
						disabled={!selectedSku || syncing}
						title="Synchroniser les prebuilds depuis api-connectors-v2 vers PostgreSQL"
					>
						{syncing ? 'Sync...' : 'Sync'}
					</Button>

					<Button
						variant="outlined"
						startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
						onClick={handleExportCSV}
						disabled={!selectedSku || exporting}
						title="Télécharger les fields au format CSV"
					>
						{exporting ? 'Export...' : 'Export CSV'}
					</Button>
				</Box>

				{/* Info convention semantic codes */}
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="subtitle2" gutterBottom>Convention QUANTI Semantic Layer</Typography>
					<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
						{SEMANTIC_PREFIXES.map((p) => (
							<Typography key={p.value} variant="body2">
								<strong>{p.value}</strong> = {p.description.split(':')[0]}
							</Typography>
						))}
					</Box>
				</Alert>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{/* Zone de drop */}
				<Box
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					sx={{
						position: 'relative',
						border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
						borderRadius: 1,
						backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
						transition: 'all 0.2s ease',
						minHeight: 200,
					}}
				>
					{/* Overlay pendant le drag */}
					{isDragging && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: 'rgba(25, 118, 210, 0.08)',
								zIndex: 10,
								borderRadius: 1,
							}}
						>
							<UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
							<Typography variant="h6" color="primary">
								Déposer le fichier CSV ici
							</Typography>
						</Box>
					)}

					{/* Overlay pendant l'import */}
					{importing && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: 'rgba(255, 255, 255, 0.9)',
								zIndex: 10,
								borderRadius: 1,
							}}
						>
							<CircularProgress sx={{ mb: 2 }} />
							<Typography variant="h6">Import en cours...</Typography>
						</Box>
					)}

					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
							<CircularProgress />
						</Box>
					) : tables.length > 0 ? (
						<TableContainer component={Paper}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell />
										<TableCell>SKU</TableCell>
										<TableCell>DW Table Name</TableCell>
										<TableCell>Nom</TableCell>
										<TableCell>Semantic Code</TableCell>
										<TableCell>Domain</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Fields</TableCell>
										<TableCell>Active</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{tables.map((table) => (
										<TableRowExpand
											key={table.id}
											table={table}
											onEditTable={handleEditTable}
											onEditField={handleEditField}
										/>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : selectedSku ? (
						<Box sx={{ p: 4, textAlign: 'center' }}>
							<Alert severity="info" sx={{ mb: 2 }}>
								Aucune table trouvée pour ce connecteur.
							</Alert>
							<Typography variant="body2" color="text.secondary">
								Glissez-déposez un fichier CSV pour importer des fields
							</Typography>
						</Box>
					) : (
						<Box sx={{ p: 4, textAlign: 'center' }}>
							<Alert severity="info" sx={{ mb: 2 }}>
								Sélectionnez un connecteur pour voir ses tables enrichies.
							</Alert>
							<Typography variant="body2" color="text.secondary">
								Vous pouvez aussi glisser-déposer un fichier CSV pour importer des fields
							</Typography>
						</Box>
					)}
				</Box>

				{/* Résultat du dernier import */}
				{lastImportResult && (
					<Alert
						severity={lastImportResult.errors?.length ? 'warning' : 'success'}
						sx={{ mt: 2 }}
						onClose={() => setLastImportResult(null)}
					>
						<Typography variant="subtitle2">
							Dernier import : {lastImportResult.updated} mis à jour, {lastImportResult.skipped} ignorés
						</Typography>
						{lastImportResult.errors && lastImportResult.errors.length > 0 && (
							<Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
								{lastImportResult.errors.slice(0, 5).map((err, i) => (
									<Typography key={i} variant="caption" display="block" color="text.secondary">
										{err}
									</Typography>
								))}
								{lastImportResult.errors.length > 5 && (
									<Typography variant="caption" color="text.secondary">
										... et {lastImportResult.errors.length - 5} autres erreurs
									</Typography>
								)}
							</Box>
						)}
					</Alert>
				)}
			</CardContent>

			{/* Dialogs */}
			<EditTableDialog
				open={editTableDialog.open}
				table={editTableDialog.table}
				onClose={() => setEditTableDialog({ open: false, table: null })}
				onSave={handleSaveTable}
			/>

			<EditFieldDialog
				open={editFieldDialog.open}
				tableId={editFieldDialog.tableId}
				field={editFieldDialog.field}
				semanticCodes={semanticCodes}
				onClose={() => setEditFieldDialog({ open: false, tableId: null, field: null })}
				onSave={handleSaveField}
			/>
		</Card>
	);
};
